<template>
   <div ref="target" class="relative" :class="{ 'w-full': grow }" @outsideClick="open = false">
      <slot name="trigger" :open="open" :toggle="toggle" />
      <ClientOnly>
         <Teleport to="body">
            <Transition
               name="fade"
               enter-active-class="transition duration-200 ease-in-out"
               enter-from-class="opacity-0 translate-y-2"
               enter-to-class="opacity-100 translate-y-0"
               leave-active-class="transition duration-200 ease-in-out"
               leave-from-class="opacity-100 translate-y-0"
               leave-to-class="opacity-0 translate-y-2"
            >
               <div
                  v-if="open"
                  ref="dropdown"
                  class="rounded-small __shadow-bottom border-light-grey fixed z-50 min-w-[250px] overflow-y-auto border bg-white p-1"
                  :style="dropdownStyle"
               >
                  <slot name="content" :open="open" :toggle="toggle" />
               </div>
            </Transition>
         </Teleport>
      </ClientOnly>
   </div>
</template>

<script lang="ts" setup>
import { onClickOutside } from '@vueuse/core'
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'

type Props = {
   minTriggerWidth?: boolean
   grow?: boolean
   minWidth?: string
}

const props = withDefaults(defineProps<Props>(), {
   minTriggerWidth: false,
})

const open = defineModel<boolean>({ default: false })

const target = ref<HTMLElement>()
const dropdown = ref<HTMLElement>()

onClickOutside(target, () => (open.value = false))

let resizeObserver: ResizeObserver | null = null

onMounted(() => {
   window.addEventListener('resize', updateDropdownPosition)
   window.addEventListener('scroll', updateDropdownPosition)
})

onUnmounted(() => {
   window.removeEventListener('resize', updateDropdownPosition)
   window.removeEventListener('scroll', updateDropdownPosition)

   if (resizeObserver) {
      resizeObserver.disconnect()
   }
})

const dropdownPosition = ref({ left: 0, top: 0, minWidth: '250px', maxHeight: '300px' })

const updateDropdownPosition = () => {
   if (!target.value || !open.value) return

   const rect = target.value.getBoundingClientRect()
   const viewportHeight = window.innerHeight

   let minWidth = '250px'
   if (props.minTriggerWidth) {
      const triggerWidth = target.value.offsetWidth
      minWidth = `${triggerWidth}px`
   } else if (props.minWidth) {
      minWidth = props.minWidth
   }

   let left = rect.right
   let top = rect.bottom + 8

   const dropdownWidth = parseInt(minWidth || '250')
   if (rect.right - dropdownWidth < 20) {
      left = rect.left
   } else {
      left = rect.right - dropdownWidth
   }

   let maxHeight = '300px'
   const availableSpace = viewportHeight - top - 20
   if (availableSpace < 300) {
      maxHeight = `${Math.max(150, availableSpace)}px`
   }

   dropdownPosition.value = {
      left,
      top,
      minWidth,
      maxHeight,
   }
}

const dropdownStyle = computed(() => {
   return {
      left: `${dropdownPosition.value.left}px`,
      top: `${dropdownPosition.value.top}px`,
      minWidth: dropdownPosition.value.minWidth,
      maxHeight: dropdownPosition.value.maxHeight,
   }
})

const toggle = () => {
   open.value = !open.value
}

watch(open, async (newValue) => {
   if (newValue) {
      await nextTick()
      updateDropdownPosition()
   }
})

watch(target, (newTarget, oldTarget) => {
   if (resizeObserver) {
      if (oldTarget) resizeObserver.unobserve(oldTarget)
      if (newTarget) resizeObserver.observe(newTarget)
   } else if (newTarget) {
      resizeObserver = new ResizeObserver(updateDropdownPosition)
      resizeObserver.observe(newTarget)
   }
})
</script>
